import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Markdown from "markdown-to-jsx/react";
import DOMPurify from "isomorphic-dompurify";

import Button from "../../ui/buttons/Button/Button";
import InfoBox from "components/content/InfoBox/InfoBox";
import { useCart } from "lib/hooks/useCart";

import styles from "./BookingInfo.module.scss";

const BookingInfo = ({
  data,
  inputHandler,
  bookingDetails,
  onBookingButtonClick,
  wasSlideOverActivated,
  currency,
  buttonRef,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCartPopupActive, setIsCartPopupActive] = useState(false);
  const { cart, addExperienceToCart } = useCart();

  // Get experience data
  const { title, id, metadata, location, seo, categories, slug, availability } =
    data;
  const { minimum_duration, minimum_duration_unit, currencies, deposit_price } =
    metadata;
  const { city, country } = location;
  const { countryName, region } = country;

  const price = currencies[currency.code.toLowerCase()] ?? "-";

  // const { peopleCount, startDate, endDate, duration } = bookingDetails;

  // const [price, setPrice] = useState(
  //   currencies ? currencies[currency.code.toLowerCase()] : false
  // );
  // const [totalPrice, setTotalPrice] = useState(
  //   currencies ? currencies[currency.code.toLowerCase()] * peopleCount : false
  // );

  // Recalculate total price whenever an input changes
  // useEffect(() => {
  //   if (totalPrice && price) {
  //     setTotalPrice(() => price * peopleCount * duration);
  //     inputHandler("totalPrice", totalPrice);
  //   }
  // }, [setTotalPrice, price, peopleCount, inputHandler, totalPrice, duration]);

  // useEffect(() => {
  //   setPrice(currencies ? currencies[currency.code.toLowerCase()] : false);
  // }, [currencies, setPrice, currency.code]);

  const categoryList = categories.map((category, index) => {
    const catName = category.category_name;
    return (
      <Link
        key={index}
        href={`/experiences/${category.slug}`}
        className={`${styles.categoryLink}`}
      >
        {catName[0].toUpperCase() + catName.slice(1, catName.length)}
      </Link>
    );
  });

  // Add to cart on booking button click
  const handleBookingButtonClick = async () => {
    setIsSubmitting(true);

    const addToCartResponse = await addExperienceToCart({
      formData: {
        people: 1,
        startDate: "None",
        duration: 1,
        product_metadata: {
          id,
          title,
          country,
          experience_code: metadata.experience_code,
          slug,
          currencies,
        },
      },
    });

    setTimeout(() => {
      setIsSubmitting(false);
      setIsCartPopupActive(true);
    }, 500);

    if (addToCartResponse?.error) {
      console.error("There was a problem adding this experience to the cart.");
      setErrorMessage(
        "There was a problem adding this experience to the cart. Please contact us directly for more information about booking this experience!"
      );
      return;
    }
  };

  return (
    <>
      <div className={styles.bookingInfoContainer}>
        <h1 className={`${styles.title}`}>{title}</h1>

        <div className={`${styles.bookingLocation}`}>
          {country?.region && country?.country && city && countryName && (
            <Link
              href={`/destinations/${country.region.region}/${country?.country}`}
              className={`${styles.locationLink}`}
            >
              <span className={`${styles.iconContainer}`}>
                <Image
                  src="/assets/img/icons/map-pin-black.svg"
                  alt="Overview icon"
                  width={15}
                  height={15}
                />
              </span>
              <span className={`${styles.locationText}`}>
                {city}, {countryName}
              </span>
            </Link>
          )}
          {(!region?.region || !country?.country || !city || !countryName) && (
            <>
              <span className={`${styles.iconContainer}`}>
                <Image
                  src="/assets/img/icons/map-pin-black.svg"
                  alt="Overview icon"
                  width={15}
                  height={15}
                />
              </span>
              <span className={`${styles.locationText}`}>
                {city}, {countryName}
              </span>
            </>
          )}
        </div>

        <div className={styles.infoBoxes}>
          <div className={`${styles.boxGroup}`}>
            <InfoBox label="Category">{categoryList && categoryList}</InfoBox>
            <InfoBox label="Style">
              {metadata.style && (
                <span className={`${styles.experienceStyle}`}>
                  {metadata.style}
                </span>
              )}
            </InfoBox>
          </div>

          <InfoBox
            classNames={`${styles.availabilityInfo}`}
            label={
              <>
                Availability
                {availability && availability.status && (
                  <>
                    :&nbsp;
                    <span
                      style={{ fontWeight: "600", color: "var(--dark-text)" }}
                    >
                      {availability.status}
                    </span>
                  </>
                )}
              </>
            }
          >
            {availability && availability?.scheduling_note && (
              <Markdown>
                {DOMPurify.sanitize(availability?.scheduling_note)}
              </Markdown>
            )}
          </InfoBox>

          <div className={`${styles.boxGroup}`}>
            {price && (
              <>
                <InfoBox
                  label="Price per person"
                  classNames={`${styles.price}`}
                >
                  <span className={`${styles.infoBoxContent}`}>
                    {currency.symbol}
                    {price}/{minimum_duration_unit}
                  </span>
                </InfoBox>
              </>
            )}
            {price && (
              <InfoBox label="Minimum Duration">
                {minimum_duration} {minimum_duration_unit}
                {minimum_duration !== 1 ? "s" : ""}
              </InfoBox>
            )}
          </div>
        </div>

        <div className={`${styles.infoButtonContainer}`}>
          <div className={`${styles.buttonWrapper}`} ref={buttonRef}>
            <Button
              classes={["checkoutButton"]}
              fill="bright"
              type="button"
              onClick={handleBookingButtonClick}
              loading={isSubmitting}
            >
              Add to Cart
            </Button>
            {/* <Button
              classes={["checkoutButton"]}
              fill="bright"
              type="button"
              onClick={onBookingButtonClick}
            >
              {wasSlideOverActivated ? "Make Enquiry" : "Make Enquiry"}
            </Button> */}
          </div>
          {errorMessage && (
            <>
              <div className={`${styles.errorContainer}`}>
                <p className={`${styles.error}`}>{errorMessage}</p>
              </div>
              <p className={`${styles.subText}`}>
                Contact us for availability and further information.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BookingInfo;
